import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Ban, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { UsersSearch } from '@/app/routes/users.index';
import { AdminLayout } from '@/app/shell/admin-layout';
import { type AdminUser, type AdminUsersQuery, useAdminUsers } from '@/shared/api/admin-users';
import { fmtDate } from '@/shared/lib/format';
import { useDebounced } from '@/shared/lib/use-debounced';
import {
  Badge,
  CopyId,
  FilterChip,
  PageHeader,
  Pagination,
  SearchInput,
  TableShell,
  TableStateRow,
  TD,
  TH,
  THEAD,
  TR,
  WIDE,
} from '@/shared/ui/kit';

const PAGE_SIZE = 50;

export function UsersListPage() {
  const search = useSearch({ from: '/users/' });
  const navigate = useNavigate({ from: '/users/' });
  const setSearch = (patch: Partial<UsersSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }), replace: true });

  // Type freely; push to the URL (and the API) once typing pauses.
  const [text, setText] = useState(search.q ?? '');
  const debounced = useDebounced(text.trim(), 300);
  useEffect(() => {
    if ((search.q ?? '') !== debounced) setSearch({ q: debounced || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const offset = search.offset ?? 0;
  const query: AdminUsersQuery = { limit: PAGE_SIZE, offset };
  if (search.q) query.search = search.q;
  if (search.filter === 'banned') query.isBanned = true;
  if (search.filter === 'admins') query.isSuperadmin = true;

  const { data, isLoading, isFetching, error } = useAdminUsers(query);

  return (
    <AdminLayout>
      <PageHeader
        title="Users"
        description="Find anyone by email, username, name or id. Open a user to verify, ban, deactivate or grant a plan — every action is audit-logged."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput value={text} onChange={setText} placeholder="Email, username, name or user id" />
        <FilterChip
          label="Banned"
          icon={<Ban size={12} />}
          active={search.filter === 'banned'}
          onClick={() => setSearch({ filter: search.filter === 'banned' ? undefined : 'banned' })}
        />
        <FilterChip
          label="Admins"
          icon={<ShieldCheck size={12} />}
          active={search.filter === 'admins'}
          onClick={() => setSearch({ filter: search.filter === 'admins' ? undefined : 'admins' })}
        />
        <div className="ml-auto text-xs text-neutral-500" aria-live="polite">
          {isFetching ? 'Loading…' : data ? `${data.total.toLocaleString()} users` : null}
        </div>
      </div>

      <TableShell>
        <thead className={THEAD}>
          <tr>
            <th className={TH}>User</th>
            <th className={TH}>Status</th>
            <th className={TH}>Plan</th>
            <th className={`${TH} ${WIDE} text-right`}>Teams</th>
            <th className={`${TH} ${WIDE}`}>Signed up</th>
            <th className={`${TH} ${WIDE}`}>Id</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          <TableStateRow
            colSpan={6}
            loading={isLoading && !data}
            error={!data ? error : undefined}
            empty={Boolean(data && data.items.length === 0)}
            emptyText={search.q ? `No users match “${search.q}”.` : 'No users match these filters.'}
          />
          {data?.items.map((u) => <UserRow key={u.id} u={u} />)}
        </tbody>
      </TableShell>

      {data ? (
        <Pagination total={data.total} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => setSearch({ offset: o || undefined })} />
      ) : null}
    </AdminLayout>
  );
}

function UserRow({ u }: { u: AdminUser }) {
  return (
    <tr className={TR}>
      <td className={TD}>
        <Link
          to="/users/$id"
          params={{ id: u.id }}
          className="block rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        >
          <div className="font-medium text-neutral-100 hover:text-orange-300">{u.name}</div>
          <div className="break-all text-xs text-neutral-400">
            {u.email} · @{u.username}
          </div>
        </Link>
      </td>
      <td className={TD}>
        <div className="flex flex-wrap gap-1">
          {u.isBanned ? (
            <Badge tone="danger">banned</Badge>
          ) : !u.isActive ? (
            <Badge tone="warn">deactivated</Badge>
          ) : (
            <Badge tone="ok">active</Badge>
          )}
          {!u.emailVerifiedAt ? <Badge tone="neutral">unverified</Badge> : null}
          {u.twoFactorEnabled ? <Badge tone="info">2FA</Badge> : null}
          {u.isSuperadmin ? <Badge tone="brand">admin</Badge> : null}
        </div>
      </td>
      <td className={`${TD} text-neutral-300`}>
        {u.personalPlanCode ?? <span className="text-neutral-600">free</span>}
        {u.personalSubscriptionStatus !== 'free' ? (
          <span className="ml-1.5 text-xs text-neutral-500">({u.personalSubscriptionStatus})</span>
        ) : null}
      </td>
      <td className={`${TD} ${WIDE} text-right tabular-nums text-neutral-300`}>{u.teamCount}</td>
      <td className={`${TD} ${WIDE} whitespace-nowrap text-neutral-400`}>{fmtDate(u.createdAt)}</td>
      <td className={`${TD} ${WIDE}`}>
        <CopyId value={u.id} short label="user id" />
      </td>
    </tr>
  );
}
